import type { BreadcrumbItem } from '@ui';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';

export type PortfolioBreadcrumbLocale = 'en' | 'es';

/** English default site uses `/portfolio` without `/en`; `[lang]` routes use `/en/portfolio` or `/es/portfolio`. */
export type PortfolioBreadcrumbOptions = {
  locale: PortfolioBreadcrumbLocale;
  prefixlessEnglish: boolean;
};

export function portfolioRootHref(opts: PortfolioBreadcrumbOptions): string {
  if (opts.locale === 'es') return '/es/portfolio';
  if (opts.prefixlessEnglish) return '/portfolio';
  return '/en/portfolio';
}

export function portfolioSectionHref(opts: PortfolioBreadcrumbOptions, sectionSlug: string): string {
  return `${portfolioRootHref(opts)}/${sectionSlug}`;
}

/**
 * Client links (e.g. ImageCard): English uses prefixless `/portfolio/...` (never `/en/portfolio`).
 * Spanish uses `/es/portfolio/...`. Absolute paths avoid relative resolution bugs like `/es/en/...`.
 */
export function portfolioPublicPath(locale: PortfolioBreadcrumbLocale, ...segments: string[]): string {
  const base = locale === 'es' ? '/es/portfolio' : '/portfolio';
  return segments.length ? `${base}/${segments.join('/')}` : base;
}

function portfolioRootLabel(locale: PortfolioBreadcrumbLocale): string {
  return String(
    renderTranslation(t('breadcrumb.portfolio_root', 'portfolio', locale)) || 'Portfolio'
  );
}

export function breadcrumbAriaLabel(locale: PortfolioBreadcrumbLocale): string {
  return String(renderTranslation(t('breadcrumb.aria_label', 'portfolio', locale)) || 'Breadcrumb');
}

export function buildPortfolioSectionBreadcrumbs(
  opts: PortfolioBreadcrumbOptions,
  sectionTitle: string,
  sectionSlug: string
): BreadcrumbItem[] {
  return [
    { label: portfolioRootLabel(opts.locale), href: portfolioRootHref(opts) },
    { label: sectionTitle, href: portfolioSectionHref(opts, sectionSlug), isCurrent: true },
  ];
}

export function buildPortfolioItemBreadcrumbs(
  opts: PortfolioBreadcrumbOptions,
  sectionTitle: string,
  sectionSlug: string,
  itemTitle: string
): BreadcrumbItem[] {
  return [
    { label: portfolioRootLabel(opts.locale), href: portfolioRootHref(opts) },
    { label: sectionTitle, href: portfolioSectionHref(opts, sectionSlug) },
    { label: itemTitle, isCurrent: true },
  ];
}
